// ReceiptUploadDialog — загрузка фискального чека (V12 U-35, волна R67).
// POST /api/payments/receipt (multipart file+entityType+entityId):
//   201 → Payment (source=QR, status=CONFIRMED) + invalidate ['payments'] — чек не хранится (SSOT U-35);
//   422 → Alert «Не удалось распознать чек — создайте платёж вручную» + кнопка «Создать вручную»
//         (родитель открывает PaymentNewDialog с preset привязки).
// Клиентская валидация: pdf/png/jpg (accept) + ≤10 МБ (лимит backend — 10MB, канон §3.5).

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { paymentsApi, type Payment } from '@/api/payments';
import { EntityBindingSelect, type PaymentBinding } from './EntityBindingSelect';
import { extractApiErrorMessage } from '@/lib/apiError';
import { t } from '@/i18n';

/** Максимальный размер файла чека (канон §3.5: ≤10МБ). */
const MAX_FILE_SIZE_MB = 10;

interface ReceiptUploadDialogProps {
  open: boolean;
  onClose: () => void;
  /** Предзаполненная привязка (блок карточки); null — выбор внутри (список платежей). */
  preset?: PaymentBinding | null;
  /** Колбэк успешной загрузки (ответ платежа для инвалидации/бейджа). */
  onUploaded?: (payment: Payment) => void;
  /** 422 → «Создать вручную»: родитель открывает PaymentNewDialog с preset. */
  onCreateManual?: (binding: PaymentBinding | null) => void;
}

/** Диалог загрузки чека (multipart FormData; файл НЕ сохраняется — SSOT U-35). */
export function ReceiptUploadDialog({
  open,
  onClose,
  preset = null,
  onUploaded,
  onCreateManual,
}: ReceiptUploadDialogProps): JSX.Element {
  const queryClient = useQueryClient();
  const [binding, setBinding] = useState<PaymentBinding | null>(preset);
  const [file, setFile] = useState<File | null>(null);
  const [sizeError, setSizeError] = useState<string | null>(null);

  // Сброс диалога под открытие: preset (карточка — статичный; список — динамический), файл, ошибки.
  useEffect(() => {
    if (open) {
      setBinding(preset);
      setFile(null);
      setSizeError(null);
    }
  }, [open, preset]);

  const isParseError = (mutationError: unknown): boolean =>
    (mutationError as { response?: { status?: number } } | null)?.response?.status === 422;

  const uploadMutation = useMutation({
    mutationFn: () => {
      if (!file || !binding) throw new Error('Файл и привязка обязательны');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entityType', binding.entityType);
      formData.append('entityId', binding.entityId);
      return paymentsApi.uploadReceipt(formData);
    },
    onSuccess: (payment) => {
      void queryClient.invalidateQueries({ queryKey: ['payments'] });
      // V19: платёж из чека меняет paidAmount/remaining счетов → рефеч сводки (канон InvoiceBlock :59).
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      onUploaded?.(payment);
      onClose();
    },
  });

  /** Валидация файла: ≤10МБ (клиентская); формат — через accept + backend magic bytes (§3.5). */
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const next = event.target.files?.[0] ?? null;
    if (next && next.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setFile(null);
      setSizeError(t('payments.upload.maxSize'));
      return;
    }
    setSizeError(null);
    setFile(next);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('payments.upload.dialogTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <EntityBindingSelect
            value={binding}
            onChange={(next) => {
              setBinding(next);
            }}
            locked={Boolean(preset)}
          />
          {/* Accept ограничивает диалог выбора файла; magic bytes проверяет backend (§3.5 п.1d). */}
          <input
            type="file"
            aria-label={t('payments.upload.file')}
            accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
            onChange={handleFileChange}
          />
          {sizeError ? <Alert severity="error">{sizeError}</Alert> : null}
          {uploadMutation.isError ? (
            <Alert
              severity="error"
              action={
                isParseError(uploadMutation.error) ? (
                  <Button color="inherit" size="small" onClick={() => onCreateManual?.(binding)}>
                    {t('payments.upload.createManual')}
                  </Button>
                ) : undefined
              }
            >
              {extractApiErrorMessage(uploadMutation.error, t('payments.upload.error'))}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={() => uploadMutation.mutate()}
          disabled={uploadMutation.isPending || !file || !binding?.entityId}
        >
          {t('payments.upload.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
